import { errorText } from "components/Forms/formsApi";
import { useEffect, useRef, useState } from "react";
import Notifications from "util/notifications";
import ActivityTabCard from "./ActivityTabCard";
import { proposalTitle, useAutomationIndex } from "./ActivityTabParts";
import { useAutomationsApi, useReference } from "./hooks";
import type { ApprovalAnswer, ApprovalItem } from "./types";

/**
 * Items waiting for approval as cards, each answered in one click: Add (or
 * Send, Move, Update), Change it first, or Not now. After a card is
 * answered, the keyboard's focus goes to the card that took its place.
 * Used by Activity, the Tasks page's Next up and a tutor's list.
 */
export default function ApprovalCards({
  approvals,
  wide = true,
}: {
  approvals: ApprovalItem[];
  /** Two cards side by side on wide screens. */
  wide?: boolean;
}) {
  const api = useAutomationsApi();
  const ref = useReference();
  const automations = useAutomationIndex();
  // Cards whose answer is on its way: a double click sends one answer.
  const sending = useRef(new Set<string>());
  const [answering, setAnswering] = useState<ReadonlySet<string>>(new Set());
  const grid = useRef<HTMLDivElement>(null);
  const [refocus, setRefocus] = useState<{ id: string; at: number } | null>(
    null
  );

  // Once the answered card has left, focus that went with it goes to the
  // card that took its place (or the one before it). Focus moved
  // elsewhere meanwhile stays where it is.
  useEffect(() => {
    if (!refocus || approvals.some((item) => item.id === refocus.id)) return;
    setRefocus(null);
    const focused = document.activeElement;
    if (focused && focused !== document.body) return;
    const buttons =
      grid.current?.querySelectorAll<HTMLButtonElement>("[data-card-yes]");
    if (buttons?.length)
      buttons[Math.min(refocus.at, buttons.length - 1)].focus();
  }, [refocus, approvals]);

  const answer = (item: ApprovalItem, at: number, reply: ApprovalAnswer) => {
    if (sending.current.has(item.id)) return;
    sending.current.add(item.id);
    setAnswering(new Set(sending.current));
    api
      .answerApproval(item.id, reply)
      .then((result) => {
        // Not now names it as its card does ("Set Stage to Won").
        Notifications.success(
          reply.decision === "dismiss"
            ? `Not now: ${proposalTitle(ref, item.proposal)} was not done.`
            : result.summary
        );
        setRefocus({ id: item.id, at });
      })
      .catch((e) =>
        Notifications.error(errorText(e, "That didn't go through. Try again."))
      )
      .finally(() => {
        sending.current.delete(item.id);
        setAnswering(new Set(sending.current));
      });
  };

  return (
    <div
      ref={grid}
      className={`grid items-start gap-4 ${wide ? "lg:grid-cols-2" : ""}`}
    >
      {approvals.map((item, at) => (
        <ActivityTabCard
          key={item.id}
          item={item}
          automations={automations}
          busy={answering.has(item.id)}
          onAnswer={(reply) => answer(item, at, reply)}
        />
      ))}
    </div>
  );
}
